import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { 
  ArrowLeft, 
  Dumbbell, 
  Calendar, 
  Trophy, 
  Flame, 
  Star,
  Clock,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WorkoutHistoryPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    try {
      const [statsRes, workoutsRes] = await Promise.all([
        axios.get(`${API}/api/workouts/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/workouts?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setWorkouts(workoutsRes.data.workouts || []);
    } catch (error) {
      console.error('Error loading workout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkout = async (workoutId) => {
    if (!window.confirm('Är du säker på att du vill ta bort detta pass?')) return;
    
    try {
      await axios.delete(`${API}/api/workouts/${workoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkouts(workouts.filter(w => w.id !== workoutId));
      loadData(); // Refresh stats
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Idag';
    if (days === 1) return 'Igår';
    if (days < 7) return `${days} dagar sedan`;
    
    return date.toLocaleDateString('sv-SE', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const formatFullDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="workout-history-page">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/fitness')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-lg font-semibold">Träningshistorik</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium">Denna vecka</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {stats?.workouts_this_week || 0}
            </p>
            <p className="text-xs text-muted-foreground">pass</p>
          </div>

          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Trophy className="w-4 h-4" />
              <span className="text-xs font-medium">Denna månad</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {stats?.workouts_this_month || 0}
            </p>
            <p className="text-xs text-muted-foreground">pass</p>
          </div>

          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <Flame className="w-4 h-4" />
              <span className="text-xs font-medium">Streak</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {stats?.workout_streak || 0}
            </p>
            <p className="text-xs text-muted-foreground">dagar i rad</p>
          </div>

          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <Dumbbell className="w-4 h-4" />
              <span className="text-xs font-medium">Totalt</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {stats?.total_workouts || 0}
            </p>
            <p className="text-xs text-muted-foreground">pass</p>
          </div>
        </div>

        {/* Extra Stats */}
        {(stats?.favorite_program || stats?.average_rating) && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-100">
            <div className="flex flex-wrap gap-4">
              {stats?.favorite_program && (
                <div>
                  <p className="text-xs text-emerald-700 font-medium">Favoritprogram</p>
                  <p className="text-sm font-semibold text-emerald-900">{stats.favorite_program}</p>
                </div>
              )}
              {stats?.average_rating && (
                <div>
                  <p className="text-xs text-emerald-700 font-medium">Genomsnittsbetyg</p>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <p className="text-sm font-semibold text-emerald-900">{stats.average_rating}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workout History */}
        <div>
          <h2 className="font-semibold text-lg mb-3">Senaste pass</h2>
          
          {workouts.length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <Dumbbell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-muted-foreground mb-4">Du har inte loggat några pass ännu</p>
              <Button onClick={() => navigate('/fitness')} className="rounded-full">
                Börja träna
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {workouts.map((workout) => (
                <div key={workout.id} className="bg-white rounded-2xl border overflow-hidden">
                  <button
                    onClick={() => setExpandedWorkout(expandedWorkout === workout.id ? null : workout.id)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{workout.day_name}</p>
                        <p className="text-xs text-muted-foreground">{workout.program_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(workout.completed_at)}
                      </span>
                      {workout.rating && (
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs font-medium">{workout.rating}</span>
                        </div>
                      )}
                      {expandedWorkout === workout.id ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                  
                  {expandedWorkout === workout.id && (
                    <div className="px-4 pb-4 border-t pt-3 space-y-3">
                      <p className="text-xs text-muted-foreground">
                        {formatFullDate(workout.completed_at)}
                        {workout.duration_minutes && ` • ${workout.duration_minutes} min`}
                      </p>
                      
                      {/* Exercises */}
                      <div className="space-y-2">
                        {workout.exercises.map((ex, i) => (
                          <div key={i} className="bg-slate-50 rounded-lg p-3">
                            <p className="font-medium text-sm mb-1">{ex.exercise_name}</p>
                            <div className="flex flex-wrap gap-2">
                              {ex.sets_completed.map((set, j) => (
                                <span key={j} className="text-xs bg-white px-2 py-1 rounded border">
                                  {set.reps} reps × {set.weight} kg
                                </span>
                              ))}
                            </div>
                            {ex.notes && (
                              <p className="text-xs text-muted-foreground mt-2 italic">{ex.notes}</p>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {workout.notes && (
                        <div className="bg-amber-50 rounded-lg p-3">
                          <p className="text-xs text-amber-800">{workout.notes}</p>
                        </div>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteWorkout(workout.id);
                        }}
                        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                        Ta bort
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
